import { Grid, Stack } from '@mui/material';
import { useEventCallback } from '@mui/material/utils';
import { pick } from 'lodash';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useDialogs } from '@work4all/components';
import { CheckboxRadioItem } from '@work4all/components/lib/input/checkbox-radio-item';
import { LabeledInput } from '@work4all/components/lib/input/labeled-input';
import { useHistoryStack } from '@work4all/components/lib/navigation/history-stack';
import {
  EventType,
  sendAmplitudeData,
} from '@work4all/components/lib/utils/amplitude/amplitude';

import { useDataMutation } from '@work4all/data';

import { FileServiceProviderConfig } from '@work4all/models/lib/Classes/FileServiceProviderConfig.entity';
import { InputFileServiceProviderConfig } from '@work4all/models/lib/Classes/InputFileServiceProviderConfig.entity';
import { EMode } from '@work4all/models/lib/Enums/EMode.enum';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import { FileServiceProviderType } from '@work4all/models/lib/Enums/FileServiceProviderType.enum';

import { PathsOf } from '@work4all/utils/lib/paths-of/paths-of';

import { EnumPickerField } from '../../../../../components/entity-picker/EnumPickerField';
import { ControllerPlus } from '../../../form-plus/controller-plus';
import { useFormContextPlus } from '../../../form-plus/use-form-context-plus';
import { Collapse } from '../../components';
import { useMaskConfig } from '../../hooks/mask-context';
import { OverlayController } from '../../overlay-controller/OverlayController';
import { useMaskOverlay } from '../../overlay-controller/use-mask-overlay';
import { MaskControllerProps } from '../../types';

import { FailAlert } from './components/fail-alert/FailAlert';
import { SharePoint365Conf } from './components/share-point-365-conf/SharePoint365Conf';
import { WebDavConf } from './components/web-dav-conf/WebDavConf';
import { useFileServiceProviderConfigRequestData } from './hooks/use-file-service-provider-config-request-data';
import { useFSPAccess } from './hooks/use-fsp-access';

export type FileServiceProviderConfigFormValue = PathsOf<
  FileServiceProviderConfig,
  2
>;

type FileServiceProviderConfigOverlayControllerProps = MaskControllerProps & {
  amplitudeEntryPoint: string;
};

const DEFAULT_VALUES = {};

const TYPE_VALUES = [
  FileServiceProviderType.SHARE_POINT_365,
  FileServiceProviderType.WEB_DAV,
];

export function FileServiceProviderConfigOverlayController(
  props: FileServiceProviderConfigOverlayControllerProps
) {
  const { amplitudeEntryPoint } = props;

  const { goBack, close, currentStackIndex, setObjectionListener } =
    useHistoryStack();

  const [mutate] = useDataMutation<FileServiceProviderConfig, EMode.upsert>({
    entity: Entities.fileServiceProviderConfig,
    inputEntity: Entities.inputFileServiceProviderConfig,
    mutationType: EMode.upsert,
    responseData: {
      id: null,
    },
  });

  const mask = useMaskConfig(props);

  const { testOneDriveConfig, testWebDavConfig } = useFSPAccess();

  useEffect(() => {
    if (mask.isCreateMode) {
      sendAmplitudeData(EventType.AddFileServiceProviderConfig, {
        entryPoint: amplitudeEntryPoint,
      });
    } else {
      sendAmplitudeData(EventType.EditFileServiceProviderConfig, {
        entryPoint: amplitudeEntryPoint,
      });
    }
  }, [amplitudeEntryPoint, mask, mask.isCreateMode, props.id]);

  const request = useFileServiceProviderConfigRequestData(mask.id);

  const { t } = useTranslation();

  const customRules = useCallback(
    (input: FileServiceProviderConfigFormValue) => {
      if (
        !input.directoryConvertPaths ||
        input.directoryConvertPaths?.length === 0 ||
        input.directoryConvertPaths?.[0]?.trim().length === 0
      ) {
        return {
          directoryConvertPaths: {
            message: t('ERROR.FIELD_REQUIRED'),
            type: 'customValidation',
          },
        };
      }
      return true;
    },
    [t]
  );

  const overlay = useMaskOverlay<FileServiceProviderConfigFormValue>({
    ...props,
    request,
    resultIsArray: true,
    newEntityData: DEFAULT_VALUES,
    mask,
    getSubTitle: (x) => x.name,
    id: mask.id,
    skipEventQuery: true,
    customRules,
  });

  const { formState } = overlay.form;

  const dialogs = useDialogs();

  const testConfig = useCallback(
    async (values: FileServiceProviderConfigFormValue) => {
      let testResult = false;

      try {
        if (values.config) {
          if (values.providerType === FileServiceProviderType.SHARE_POINT_365) {
            testResult =
              (await testOneDriveConfig(values.config))?.data
                ?.testFileServiceProviderConfig || false;
          }
          if (values.providerType === FileServiceProviderType.WEB_DAV) {
            testResult =
              (await testWebDavConfig(values.config))?.data
                ?.testFileServiceProviderConfig || false;
          }
        }
      } catch (err) {
        console.warn(err);
      }

      if (!testResult) {
        dialogs.alert({
          title: t('COMMON.FSP.CONNECTION_TEST.TITLE'),
          description: <FailAlert />,
        });
      }

      return testResult;
    },
    [dialogs, t, testOneDriveConfig, testWebDavConfig]
  );

  const handleSubmit = useEventCallback(
    async (values: FileServiceProviderConfigFormValue) => {
      if (!values.id) {
        const testResult = await testConfig(values);
        if (!testResult) {
          return;
        }
      }

      const update: InputFileServiceProviderConfig = mask.isCreateMode
        ? pick(
            values,
            Object.keys(values).filter((key) => key !== 'directoryConvertPaths')
          )
        : pick(values, ['id', ...Object.keys(formState.dirtyFields)]);

      update.convertPath = values.directoryConvertPaths?.[0] || '';

      if (update.providerType === FileServiceProviderType.WEB_DAV) {
        update.configWebDav = values.config;
      }

      if (update.providerType === FileServiceProviderType.SHARE_POINT_365) {
        update.configSharePoint = values.config;
      }

      await mutate(update);

      setObjectionListener(null);
      sendAmplitudeData(EventType.SaveTenant, {
        entryPoint: amplitudeEntryPoint,
      });
      /**
       * TODO: WW-6730
       */
      if (currentStackIndex !== 0) {
        goBack();
      } else {
        close();
      }
    }
  );

  return (
    <OverlayController<FileServiceProviderConfigFormValue>
      {...overlay}
      onSubmit={handleSubmit}
    >
      <FileServiceProviderConfigMaskContent />
    </OverlayController>
  );
}

function FileServiceProviderConfigMaskContent() {
  const { t } = useTranslation();

  const { register, control, watch } =
    useFormContextPlus<FileServiceProviderConfigFormValue>();

  const type = watch('providerType');
  const id = watch('id');

  return (
    <Stack>
      <Collapse title={t('COMMON.GENERAL')} defaultOpen>
        <Grid container spacing="1rem">
          <Grid item xs={12} sm={6} md={8}>
            <LabeledInput label={t('COMMON.NAME')} {...register('name')} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <ControllerPlus
              control={control}
              name="providerType"
              render={({ field }) => {
                return (
                  <EnumPickerField
                    values={TYPE_VALUES}
                    label={t('COMMON.FILE_SERVICE_PROVIDER_TYPE')}
                    translationPrefix="FILE_SERVICE_PROVIDER_TYPE"
                    {...field}
                    disabled={!!id}
                  />
                );
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={8}>
            <ControllerPlus
              control={control}
              name="directoryConvertPaths"
              render={({ field }) => {
                return (
                  <LabeledInput
                    error={field.error}
                    label={t('COMMON.LOCAL_DIRECTORY')}
                    defaultValue={field.value?.[0] || ''}
                    onChange={(e) => field.onChange([e.target.value])}
                  />
                );
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <LabeledInput label={t('COMMON.ID')} {...register('id')} disabled />
          </Grid>

          <Grid item xs={12} sm={12} md={12}>
            <Stack direction="row" gap="1rem">
              <ControllerPlus
                control={control}
                name="isWorkDirectory"
                render={({ field }) => {
                  return (
                    <>
                      <CheckboxRadioItem
                        label={t('COMMON.WORK4ALL_DIRECTORY')}
                        checked={field.value === true}
                        onChange={() => field.onChange(true)}
                        control="radio"
                      />
                      <CheckboxRadioItem
                        label={t('COMMON.PROJECT_DIRECTORY')}
                        checked={field.value === false}
                        onChange={() => field.onChange(false)}
                        control="radio"
                      />
                    </>
                  );
                }}
              />
            </Stack>
          </Grid>
        </Grid>
      </Collapse>
      {type && type !== FileServiceProviderType.LOCAL_FILE_SYSTEM && (
        <Collapse title={t('COMMON.CONFIGURATION')} defaultOpen>
          {type === FileServiceProviderType.SHARE_POINT_365 && (
            <SharePoint365Conf />
          )}
          {type === FileServiceProviderType.WEB_DAV && <WebDavConf />}
        </Collapse>
      )}
    </Stack>
  );
}
